import type {
  AiToolAction,
  ElementCategory,
  LifecyclePhase,
  ProductionFormat,
  ProductionModel,
  ProjectHealth,
  RequirementSourcingStatus,
  SceneAnalysisState,
  SceneExclusionReason,
  SceneIntExt,
  SceneValidationStatus,
  SceneValidationWarning,
  WorkflowStatus,
} from '@scoutaround/script-breakdown-shared';
import { TERMS_ACCEPTANCE_REQUIRED, WORKSPACE_ROLE_REQUIRED } from '@scoutaround/script-breakdown-shared';
import type { OverviewActivityItem, ProjectOverview } from '@scoutaround/script-breakdown-shared';
import type {
  EnterResult,
  JoinDecisionResult,
  JoinLinkCreated,
  JoinLinkPreview,
  JoinLinkView,
  JoinRequestList,
  JoinerView,
  MyJoinRequest,
  CreateReferenceInput,
  LinkPreview,
  ReferenceCategory,
  ReferenceDetail,
  ReferenceLibraryMeta,
  ReferenceLinkInput,
  ReferenceLinkTargetOption,
  ReferenceLinkTargetType,
  ReferenceListPage,
  ReferenceListQuery,
  UpdateReferenceInput,
  UploadReferenceMetadata,
} from '@scoutaround/script-breakdown-shared';
import type {
  ConversationAnchorType,
  ConversationEmailPreference,
  ConversationFollowState,
  ConversationListFilter,
  ConversationLocations,
  ConversationMessageView,
  ConversationReportReason,
  ConversationSummary,
  ConversationThreadView,
  ConversationVisibility,
} from '@scoutaround/script-breakdown-shared';
import type { ProjectFileDeleted, ProjectFilesMeta, ProjectFileView } from '@scoutaround/script-breakdown-shared';

import { CLIENT_SITE_URL } from './config.js';

const TOKEN_KEY = 'sb.token';

/**
 * Where a Terms refusal keeps the page to come back to (this tab only):
 * `EnterPage` reads it on `/enter?resume=1`. Not in the URL — see `request()`.
 */
export const TERMS_RESUME_KEY = 'sb.terms_resume';

/** Set once the browser has been sent to the Terms screen; a page load resets it. */
let termsRedirectStarted = false;

/**
 * Back from the website can restore THIS page from the back-forward cache,
 * module state and all — the flag still set, so a later Terms 403 would never
 * redirect again. A restored page (`persisted`) starts over.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', (event: PageTransitionEvent) => {
    if (event.persisted) termsRedirectStarted = false;
  });
}

export function getToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    // Private windows and blocked site data throw rather than returning null.
    return null;
  }
}

export function setToken(token: string): void {
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* a session that cannot persist still works for this tab */
  }
}

export function clearToken(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* nothing to clear */
  }
}

type SessionExpiredListener = () => void;

const sessionExpiredListeners = new Set<SessionExpiredListener>();

/**
 * Fires when the server refuses the token this tab is holding.
 *
 * WHY IT IS NOT ENOUGH TO HAVE A TOKEN. `App` decided whether somebody was
 * signed in by asking whether `sessionStorage` HAD a token — a question the
 * browser can answer and the server disagrees with the moment the JWT expires.
 * The stale token then rode along on every request, the project list 401'd,
 * react-query surfaced an error, and `WorkspacePage` — which resolves its
 * address against that list — concluded the project did not exist and said so:
 * "Project not found.", on a production that is perfectly fine. A refresh
 * repeated it exactly, because the expired token was still sitting in
 * `sessionStorage` and still looked like a session.
 *
 * So the ONE place that learns the truth is the response, and it has to tell
 * the app. A listener rather than a redirect from here: this module must not
 * decide what an expiry looks like, and a hard `location.assign` out of a
 * fetch would discard whatever the producer had typed with no word of warning.
 */
export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(listener);

  return () => {
    sessionExpiredListeners.delete(listener);
  };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The API's 403 for a signed-in account that does not hold the
 * `production_workspace` role (ActorGuard). Not a failure to retry: only
 * ScoutAround can grant the role, and accepting an invitation does not
 * (ADR 0010), so a screen that meets it says so and points at the contact page.
 * The code comes from `shared/`, the definition the API answers with.
 */
export { WORKSPACE_ROLE_REQUIRED };

export function isWorkspaceRoleRequired(error: unknown): error is ApiError {
  return error instanceof ApiError && error.code === WORKSPACE_ROLE_REQUIRED;
}

/**
 * Same-origin `/api` everywhere — nginx in production, Vite's proxy in dev. The
 * SPA never learns the API's hostname, so there is no cross-origin preflight and
 * no baked-in URL to go stale between environments.
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const isForm = init.body instanceof FormData;

  /**
   * A CONTENT-TYPE IS A PROMISE ABOUT A BODY, so a request without one must not
   * make it.
   *
   * This helper used to send `content-type: application/json` on every
   * non-multipart request, body or not. Fastify takes that header literally and
   * refuses with `Body cannot be empty when content-type is set to
   * 'application/json'` — so every bodyless verb 400'd before reaching its
   * handler. Four of them: running a match, accepting an invitation, revoking
   * one, and removing a reference.
   *
   * It looked like four unrelated broken features and was one header. Worth
   * remembering which layer it lived in: the invitation service has nine tests
   * and all of them pass — they exercise the service, and nothing exercised the
   * request that reaches it.
   */
  const hasBody = init.body !== undefined && init.body !== null;

  const response = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(isForm || !hasBody ? {} : { 'content-type': 'application/json' }),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  /**
   * A 401 ON A REQUEST THAT CARRIED A TOKEN MEANS THAT TOKEN IS DEAD — expired,
   * or revoked because the producer signed in somewhere else (the platform
   * allows one active JWT per user). It is cleared here rather than left to
   * mislead the next render, and the app is told so it can show the sign-in
   * route instead of whatever empty-shaped answer the failed query produces.
   *
   * Only when a token was actually sent. Redeeming a handoff ticket and
   * previewing an invitation are unauthenticated by design, and their 401 is a
   * bad link, not a lost session — turning that into a sign-out would bounce
   * the invited user away from the page the invitation exists to show them.
   */
  noteRefusedToken(response.status, token);

  if (response.status === 204) return undefined as T;

  const raw = await response.text();
  const parsed: unknown = raw ? JSON.parse(raw) : null;

  if (!response.ok) {
    const body = parsed as { code?: string; message?: string } | null;

    /**
     * THE TERMS SCREEN IS THE WEBSITE'S (spec
     * docs/superpowers/specs/2026-10-29-terms-acceptance-gate-design.md §6).
     * `ActorGuard` answers this 403 on every route until the account accepts,
     * so no screen here could do anything useful with it: the browser goes to
     * the website's screen.
     *
     * THE PAGE TO COME BACK TO STAYS IN THIS TAB. An invitation page carries
     * its token in its path, and a `return` naming it would put the token in
     * the website's request line, its logs and its history. So the path is
     * kept in sessionStorage and `return` is always `/enter?resume=1` on this
     * origin — the website accepts it (`TermsGate::isWorkspaceUrl()`), and
     * `EnterPage` resumes from the stored path. The token is kept too: owing an
     * acceptance is not being signed out, and after Accept the next request
     * gets in.
     *
     * ONCE PER PAGE: a screen's parallel queries all meet the same 403. Still
     * thrown, so no caller treats the refusal as an answer while the page leaves.
     */
    if (response.status === 403 && body?.code === TERMS_ACCEPTANCE_REQUIRED && !termsRedirectStarted) {
      termsRedirectStarted = true;
      try {
        sessionStorage.setItem(
          TERMS_RESUME_KEY,
          window.location.pathname + window.location.search + window.location.hash,
        );
      } catch {
        // Not kept: `EnterPage` falls back to the landing page.
      }

      const resume = `${window.location.origin}/enter?resume=1`;
      window.location.assign(`${CLIENT_SITE_URL}/terms/accept?return=${encodeURIComponent(resume)}`);
    }

    // The API returns machine-readable codes so a screen can explain a refusal
    // rather than showing "something went wrong" — the same shape uploads use.
    throw refusal(response.status, parsed);
  }

  return parsed as T;
}

/** The 401 rule above, shared with the upload transport below so the two cannot disagree. */
function noteRefusedToken(status: number, token: string | null): void {
  if (status === 401 && token !== null) {
    clearToken();
    for (const listener of [...sessionExpiredListeners]) listener();
  }
}

/** The API returns machine-readable codes so a screen can explain a refusal
 *  rather than showing "something went wrong". */
function refusal(status: number, parsed: unknown): ApiError {
  const body = parsed as { code?: string; message?: string } | null;

  return new ApiError(status, body?.code ?? null, body?.message ?? `Request failed (${String(status)})`, parsed);
}

export interface UploadProgress {
  loaded: number;
  total: number;
}

export interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
}

/** What a cancelled upload rejects with, so a screen can tell "you stopped it" from "it failed". */
export function isCancelledUpload(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * A FILE UPLOAD THAT CAN SAY HOW FAR IT HAS GOT.
 *
 * `fetch` reports nothing about the request body while it is being sent, so a
 * 30 MB screenplay on a hotel connection was a spinner for a minute with no
 * way to tell slow from stuck. `XMLHttpRequest` still is the only browser API
 * with upload progress. Everything else is `request`'s: the bearer, the 401
 * rule and the refusal shape go through the same two helpers.
 *
 * A body nginx refuses (413) arrives as HTML, not JSON — read as no body
 * rather than thrown as a parse error, and named for what it is.
 */
function uploadWithProgress<T>(path: string, body: FormData, options: UploadOptions): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (options.signal?.aborted) {
      reject(new DOMException('The upload was cancelled', 'AbortError'));

      return;
    }

    const token = getToken();
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api${path}`);
    if (token) xhr.setRequestHeader('authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) options.onProgress?.({ loaded: event.loaded, total: event.total });
    };
    xhr.onload = () => {
      noteRefusedToken(xhr.status, token);

      let parsed: unknown;
      try {
        parsed = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        parsed = null;
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parsed as T);

        return;
      }

      reject(
        xhr.status === 413 && parsed === null
          ? new ApiError(413, 'FILE_TOO_LARGE', 'That file is larger than the 30 MB limit.')
          : refusal(xhr.status, parsed),
      );
    };
    xhr.onerror = () => {
      reject(new ApiError(0, null, 'The upload did not reach the server. Check your connection and try again.'));
    };
    xhr.onabort = () => {
      reject(new DOMException('The upload was cancelled', 'AbortError'));
    };
    options.signal?.addEventListener('abort', () => {
      xhr.abort();
    }, { once: true });

    xhr.send(body);
  });
}

export interface Project {
  id: string;
  /**
   * The short key this project's URL is built from. Nullable only in the window
   * between the column migration and its backfill reaching a row — a readable
   * address is a courtesy, never the thing that makes a page unreachable.
   */
  urlKey: string | null;
  title: string;
  format: ProductionFormat;
  productionModel: ProductionModel;
  lifecyclePhase: LifecyclePhase;
  derivedHealth: ProjectHealth;
  healthOverride: ProjectHealth | null;
  /**
   * When the guided setup was finished — null while the production is still
   * on its first pass, which is when the list offers "Continue setup". The
   * step to continue AT is the server's `nextStep`, asked for on the way in.
   */
  setupCompletedAt: string | null;
}

export interface Scene {
  id: string;
  ordinal: number;
  sceneNumber: string | null;
  slugline: string | null;
  intExt: string | null;
  timeOfDay: string | null;
}

/**
 * What the worker has actually done, as the worker recorded it.
 *
 * TWO STAGES, NOT THE DESIGN'S SIX. `PARSE` and `EXTRACT` are what this service
 * runs; the rest of the mock's list is either inside one of them or belongs to
 * matching, which a producer starts themselves.
 */
export interface ScriptProgress {
  runId: string;
  status: string;
  failureCode: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  stages: {
    stage: string;
    status: string;
    completedUnits: number;
    /** Known for extraction, which counts scenes; null for a parse. */
    totalUnits: number | null;
    failedUnits: number;
    failureCode: string | null;
  }[];
}

/**
 * WHERE A PRODUCTION IS ON ITS GUIDED FIRST PASS — the server's one answer.
 *
 * Computed once, there: the flow, the Projects list and the Overview all ask
 * "how far has this production got", and three screens each deriving it would
 * be three rules that disagree. `nextStep` is the redirect target; `null` once
 * the producer has finished — which depends on the finish bookmark and on
 * nothing the matching engine found.
 */
export interface ProjectSetup {
  hasScript: boolean;
  /** When the scene structure was confirmed — "Confirm scenes & continue". */
  scenesConfirmedAt: string | null;
  /** The same bookmark under its old name, kept for older clients. */
  scenesOverviewCompletedAt: string | null;
  reviewComplete: boolean;
  /**
   * Set once, by step 3's "Continue anyway": the producer went on to locations
   * with requirements still undecided. Not `reviewComplete` — those stay
   * PROPOSED and are simply not searched for until somebody confirms them.
   */
  breakdownContinuedAt: string | null;
  approvedCount: number;
  searchedCount: number;
  /** Which approved requirements have a match run — the only honest source of "searched". */
  searchedRequirementIds: string[];
  matchedLocationCount: number;
  setupCompletedAt: string | null;
  /**
   * Two shared steps, then the choice of workflows — never one workflow, since
   * locations and the detailed breakdown are independent (owner brief, 2026-10-29).
   */
  nextStep: 'start' | 'scenes' | 'choose' | null;
  /** Each workflow from its own rows; finishing one says nothing about the other. */
  workflows: { locations: WorkflowStatus; breakdown: WorkflowStatus };
  sceneCount: number;
  breakdownReviewedScenes: number;
  /** Scenes changed since the AI breakdown read them. */
  outOfDateSceneCount: number;
}

/** One scene as step 2 validates it — every status computed by the server. */
export interface SceneValidationRow {
  id: string;
  ordinal: number;
  sceneNumber: string | null;
  slugline: string | null;
  /** The heading as the parser read it, when a person has since edited it. */
  originalSlugline: string | null;
  intExt: string | null;
  timeOfDay: string | null;
  /** BREAKDOWN — the parser found it; MANUAL — a person made it. */
  origin: string;
  text: string | null;
  textSource: 'SCREENPLAY' | 'TYPED' | null;
  textTruncated: boolean;
  status: SceneValidationStatus;
  warnings: SceneValidationWarning[];
  confirmedAt: string | null;
  suggestedSplitOffsets: number[];
  analysis: SceneAnalysisState;
  /** Changed after the AI read it, or added with text after the screenplay was read. */
  needsReading: boolean;
  exclusionReason: SceneExclusionReason | null;
  mergedIntoSceneId: string | null;
  excludedAt: string | null;
}

export interface SceneValidationView {
  scriptVersionId: string;
  scenes: SceneValidationRow[];
  excluded: SceneValidationRow[];
  summary: {
    detected: number;
    active: number;
    confirmed: number;
    toReview: number;
    needsAttention: number;
    excluded: number;
    outOfDate: number;
    /** Scenes with text the AI has not read, in a screenplay it has read. */
    unread: number;
  };
}

export interface Requirement {
  id: string;
  title: string;
  status: string;
  sourcingStatus: RequirementSourcingStatus;
  requirementVersion: number;
}

export interface ReviewScene {
  sceneId: string;
  ordinal: number | null;
  sceneNumber: string | null;
  slugline: string | null;
}

export interface ReviewConstraint {
  id: string;
  kind: string;
  label: string;
  provenance: string;
}

export interface ReviewRequirement {
  id: string;
  title: string;
  status: string;
  sourcingStatus: RequirementSourcingStatus;
  origin: string;
  confidenceBand: string;
  confidenceReason: string | null;
  creativeDescription: string | null;
  scriptReferences: string[] | null;
  searchCountry: string | null;
  searchCity: string | null;
  searchRadiusKm: number | null;
  needsRevisionReview: boolean;
  reviewReason: string | null;
  rowVersion: number;
  requirementVersion: number;
  /** Whether there is anything a listing can be assessed against — a type or a
   *  must-have. Without one, Explore offers guidance rather than a search. */
  hasMatchableSignal: boolean;
  scenes: ReviewScene[];
  constraints: ReviewConstraint[];
}

export interface ReviewProgress {
  total: number;
  sceneCount: number;
  /** Scenes extraction proposed nothing for — a review task, not an absence. */
  scenesWithoutRequirements: number;
  approved: number;
  proposed: number;
  dismissed: number;
  sourcing: number;
  reviewComplete: boolean;
}

export interface ScheduleWarning {
  code: string;
  message: string;
  assignmentId: string;
  scheduledCandidateId: string;
  currentPrimaryCandidateId: string;
}

export interface AxisVerdict {
  state: 'STRONG' | 'PARTIAL' | 'WEAK';
  reasoning: string;
  hardCapped: boolean;
}

export interface MustHaveVerdict {
  valueId: string;
  state: 'VERIFIED' | 'UNKNOWN' | 'FAILS';
  note: string;
  provenance: string;
}

export interface CompareCandidate {
  candidateId: string;
  sourceType: string;
  sourceId: number;
  status: string;
  displayName: string;
  selectable: boolean;
  mustHaves: MustHaveVerdict[];
  axes: Record<string, AxisVerdict>;
  stale: boolean;
}

export interface CompareView {
  requirementId: string;
  requirementTitle: string;
  requirementStatus: string;
  primaryCandidateId: string | null;
  backupCandidateId: string | null;
  selectionVersion: number;
  candidates: CompareCandidate[];
}

/** A listing from inventory search. No position: the marketplace never
 *  publishes a listing's exact point, and the search screen plots nothing. */
export interface ListedLocation {
  sourceType: 'LOCATION';
  sourceId: number;
  title: string;
  city: string | null;
  countryCode: string | null;
  typeName: string | null;
}

/** A scout's pin, coarsened. Never the real name, never an exact position. */
export interface ProducerSafePoi {
  sourceType: 'POI';
  sourceId: number;
  displayLabel: string;
  approximateLatitude: number | null;
  approximateLongitude: number | null;
  countryCode: string | null;
  discoveredMonth: string | null;
  evidenceCoverage: string;
  readiness: string;
}

export interface InventorySearch {
  locations: ListedLocation[];
  pois: ProducerSafePoi[];
  /** Hit the cap — the UI says "narrow this" rather than implying completeness. */
  truncated: boolean;
}

export interface PlanFeatures {
  locationManagement: boolean;
  /** The only quantity in the matrix: both paid tiers grant the AI scout for a
   *  single script. 0 disables it. */
  aiScriptLimit: number;
  marketInsights: boolean;
  mediaStorage: boolean;
  exclusiveLocations: boolean;
  collaboration: boolean;
  scheduling: boolean;
  fastEquipment: boolean;
  personalizedSupport: boolean;
}

export interface ProjectEntitlement {
  /** FREE and EXPIRED both run on the free tier's features; only EXPIRED is
   *  read-only. Never collapse them. */
  state: 'FREE' | 'PAID' | 'EXPIRED';
  planCode: string;
  termMonths: number | null;
  periodStartsAt: string | null;
  periodEndsAt: string | null;
  readOnly: boolean;
  features: PlanFeatures;
}

export interface PlanPrice {
  termMonths: number;
  amountMinor: number;
  currency: string;
  /** Display only — the charge is always `amountMinor`. */
  monthlyEquivalentMinor: number;
}

export interface Plan {
  code: string;
  name: string;
  description: string | null;
  isFree: boolean;
  features: PlanFeatures;
  prices: PlanPrice[];
}

export interface StartedPurchase {
  purchaseUuid: string;
  amountMinor: number;
  currency: string;
  clientSecret: string | null;
  publishableKey: string;
}

/**
 * The References library's list query as the SPA sends it (`ReferenceListQuery`,
 * shared): every field optional, the server applies the defaults.
 */
export type ReferenceLibraryQuery = Partial<Omit<ReferenceListQuery, 'category'>> & {
  category?: ReferenceCategory;
};

export interface BreakdownItem {
  id: string;
  name: string;
  /** Drives the chip styling: a producer must be able to see at a glance what
   *  they have signed off from what a model suggested. */
  provenance: 'AI_INFERENCE' | 'HUMAN_CONFIRMED';
  confidenceBand: string | null;
}


export interface CountryIncentiveRow {
  iso: string;
  name: string;
  headlineRate: number;
  label: string;
  maxRate: number | null;
  kind: string;
}

export interface SearchParametersState {
  countries: string[];
  city: string | null;
  radiusKm: number | null;
  shootWindowStart: string | null;
  shootWindowEnd: string | null;
  budgetBand: string | null;
  studioPreference: string | null;
  interiorExterior: string | null;
  twinRequired: boolean;
  constraintsNote: string | null;
  /** Null means never answered — NOT the same as "no constraints". */
  setAt: string | null;
}

export interface CountryRecommendation {
  iso: string;
  name: string;
  listingCount: number;
  incentiveLabel: string | null;
  incentiveKind: string | null;
  reason: string;
}

export interface SearchParametersView {
  current: SearchParametersState;
  recommended: { countries: CountryRecommendation[]; disclaimer: string };
  catalogue: CountryIncentiveRow[];
}

export interface MatchedScene {
  sceneId: string;
  sceneNumber: string | null;
  slugline: string | null;
  excerpt: string | null;
}

export interface MyMembership {
  role: string;
  label: string;
  permissions: string[];
  /** The signed-in person — which messages are their own. Absent from an older API. */
  userId?: number;
  /** Platform switches riding on the session (ADR 0021). Absent from an older API: off. */
  features?: { conversations: boolean; hostMessages: boolean };
}

export interface RolePermissionCell {
  permission: string;
  label: string;
  /** Spends real money — shown so a producer never hands one out by accident. */
  spends: boolean;
  /** Membership itself. Nobody can change it. */
  locked: boolean;
}

export interface RolePermissionGrid {
  features: {
    feature: string;
    label: string;
    actions: { action: string; label: string; cells: RolePermissionCell[] }[];
  }[];
  roles: {
    role: string;
    label: string;
    department: string;
    departmentLabel: string;
    summary: string;
    permissions: {
      permission: string;
      granted: boolean;
      /** What the catalogue says — so "changed" is drawable. */
      byDefault: boolean;
      state: 'GRANTED' | 'REVOKED' | 'DEFAULT';
    }[];
  }[];
}

export interface MatchedRequirement {
  requirementId: string;
  title: string;
  candidateId: string;
  candidateStatus: string;
  /** For the compare-and-swap on every candidate write. Never assume 1. */
  candidateVersion: number;
  addedVia: string | null;
  /** False for a candidate a producer added by hand — it has no verdicts, which
   *  is an ABSENT assessment and not a failed one. */
  assessed: boolean;
  coverage: { verified: number; total: number };
  axes: Record<string, { state: string; reasoning: string; hardCapped: boolean }>;
  mustHaves: { valueId: string; state: string; note: string }[];
  conflicts: boolean;
  scenes: MatchedScene[];
}

export interface UnmatchedRequirement {
  id: string;
  title: string;
  status: string;
  confidenceBand?: string | null;
  confidenceReason?: string | null;
  /** What the place has to be — the brief a coordinator is sent. */
  description?: string | null;
  /** "Sofia, BG", or null when the requirement narrows to nowhere. */
  target?: string | null;
  scenes?: string[];
}

export interface MatchedLocation {
  sourceType: string;
  sourceId: number;
  title: string;
  city: string | null;
  countryIso: string | null;
  typeCode: string | null;
  rateAmount: number | null;
  priceOnRequest: boolean;
  /** Null for a pin: its photographs are scout-report material. */
  imageUrl: string | null;
  latitude: number | null;
  longitude: number | null;
  requirements: MatchedRequirement[];
}

export interface MatchedView {
  requirementCount: number;
  coveredRequirementCount: number;
  locations: MatchedLocation[];
  unmatched: UnmatchedRequirement[];
}

export interface MergeSuggestion {
  requirementIds: string[];
  titles: string[];
  scenes: string[];
  reason: string;
}

export interface ReviewViewMark {
  /** The mention's own id — what every correction verb is keyed by. */
  id: string;
  text: string;
  category: ElementCategory;
  /** The model proposed it and nobody has confirmed it — rendered as an
   *  outline rather than a fill, so a proposal never looks like a decision. */
  unconfirmed: boolean;
  /**
   * What a human last said about it: ACCEPT, REJECT, EDIT and the rest, or null
   * while nobody has said anything. A rejected element is struck through rather
   * than removed — the producer needs to see what the model read even when they
   * disagreed with it, and needs the way back.
   */
  decision: string | null;
  startOffset: number | null;
  endOffset: number | null;
}

/** An element the production has agreed is on this scene's sheet. */
export interface ReviewViewElement {
  id: string;
  name: string;
  category: ElementCategory;
  /**
   * How many this scene needs. Null means nobody has counted, which is NOT one:
   * a planning figure derived from an uncounted element would be a guess
   * wearing the authority of a number.
   */
  quantity: number | null;
}

export interface ReviewViewRequirement {
  id: string;
  title: string;
  status: string;
  origin: string;
  confidenceBand: string;
  confidenceReason: string | null;
  reviewStatus: string;
  /** The OTHER scenes this requirement covers — the warning that an edit here
   *  reaches further than the card it is made on. */
  sharedWithSceneNumbers: string[];
}

export interface ReviewViewScene {
  id: string;
  ordinal: number;
  sceneNumber: string | null;
  slugline: string | null;
  intExt: string | null;
  timeOfDay: string | null;
  synopsis: string | null;
  reviewStatus: string;
  text: string | null;
  marks: ReviewViewMark[];
  /**
   * The CONFIRMED half of the sheet — what a producer added by hand, or
   * accepted from the model. `marks` is what the model read; the two are merged
   * on screen exactly as the Script & Scenes panel merges them.
   */
  elements: ReviewViewElement[];
  requirements: ReviewViewRequirement[];
}

export interface ReviewView {
  scenes: ReviewViewScene[];
  requirements: { id: string; title: string; status: string; sceneCount: number }[];
}

export interface SceneBreakdown {
  sceneId: string;
  sceneNumber: string | null;
  slugline: string | null;
  intExt: string | null;
  timeOfDay: string | null;
  /** EVERY panel category, empty ones included — the server sends them all and
   *  the panel renders them all. An empty Stunts row states this scene has
   *  none, which is not the same as nobody having looked. */
  categories: { category: ElementCategory; items: BreakdownItem[] }[];
  locations: { requirementId: string; title: string; status: string }[];
}

export interface Member {
  id: string;
  userId: number;
  authorizationRole: string;
  productionTitle: string | null;
  status: string;
  permissions: string[];
}

export interface Invitation {
  id: string;
  email: string;
  authorizationRole: string;
  productionTitle: string | null;
  status: string;
  expiresAt: string;
  grantedPlatformRole: boolean;
}

export interface ActivityEntry {
  id: string;
  action: string;
  subjectType: string | null;
  actorUserId: number | null;
  createdAt: string;
  payload: Record<string, unknown> | null;
}

/** A Files tab row (`GET …/files`): the server's view, never the stored row. */
export type ProjectFile = ProjectFileView;

/** A conversation the producer is ALREADY a party to, narrowed to this
 *  project. The workspace stores no message and mints no second inbox — the
 *  href goes to the platform's own. */
/** A member of a production, by name. `canReadAnchorThreads` is set only when the
 *  list was asked for one anchor type: may they read a public thread on it. */
export interface ProjectPerson {
  userId: number;
  displayName: string | null;
  role: string;
  memberStatus: string;
  canReadAnchorThreads: boolean | null;
}

export interface ProjectConversation {
  threadId: number;
  contextType: 'location' | 'booking';
  contextId: number;
  lastMessageAt: string | null;
  unread: boolean;
  href: string;
}

export interface WorkspaceRequest {
  id: string;
  reference: string;
  type: string;
  title: string;
  status: string;
  deadline: string | null;
  ownerUserId: number | null;
  result: string | null;
  targets: { targetType: string; targetId: string; targetRole: string }[];
}

export interface Strip {
  sceneId: string;
  sceneNumber: string | null;
  intExt: string | null;
  slugline: string | null;
  timeOfDay: string | null;
  pageEighths: number | null;
  daySequence: number | null;
  cast: string[];
}

export interface Dood {
  days: number[];
  rows: { castName: string; cells: string[]; workDays: number }[];
}

export const api = {
  /**
   * Spend the AI budget on a screenplay already uploaded. A deliberate act: one
   * paid model request for the whole screenplay, so it is never done on the
   * producer's behalf.
   *
   * `sceneIds` narrows it to part of the script. That exists for a screenplay
   * longer than one request can carry — the run reports which scenes it could
   * not take, and this is how those, or any others, are asked for on their own.
   * Omitting it means the whole screenplay, which is every ordinary run; the
   * server reads no body as exactly that, so an older SPA keeps working.
   */
  requestExtraction: (projectId: string, scriptVersionId: string, sceneIds?: readonly string[]) =>
    request<{ pipelineRunId: string; enqueued: boolean; sceneCount: number | null }>(
      `/v1/projects/${projectId}/script-versions/${scriptVersionId}/extract`,
      {
        method: 'POST',
        ...(sceneIds === undefined ? {} : { body: JSON.stringify({ sceneIds: [...sceneIds] }) }),
      },
    ),

  /** Exchange a client-site handoff ticket for this user's existing session.
   *  Unauthenticated: it is how a session begins. */
  /** `state` is the one this tab minted before going to the client site; the
   *  client site bound the ticket to it and refuses a mismatch at redeem. */
  redeemHandoff: (ticket: string, state: string) =>
    request<{ token: string }>('/v1/auth/handoff', {
      method: 'POST',
      body: JSON.stringify({ ticket, state }),
    }),

  /** What an invitation is for, before anyone commits to it. Unauthenticated
   *  on purpose — the recipient may not have an account yet, which is the
   *  whole case this feature exists for. */
  previewInvitation: (token: string) =>
    request<{
      projectTitle: string;
      email: string;
      authorizationRole: string;
      productionTitle: string | null;
      state: 'PENDING' | 'EXPIRED' | 'ACCEPTED' | 'REVOKED';
    }>(`/v1/invitations/${encodeURIComponent(token)}`),

  acceptInvitation: (token: string) =>
    request<{ projectId: string; alreadyMember: boolean }>(
      `/v1/invitations/${encodeURIComponent(token)}/accept`,
      { method: 'POST' },
    ),

  // ─── Invitation links (2026-11-02). The token is ALWAYS a JSON body, never a
  // path or a query: it reaches no request line and no Referer. ───────────────

  /** Signed out: the production's title for a usable link; 404 JOIN_LINK_INVALID otherwise. */
  previewJoinLink: (token: string) =>
    request<JoinLinkPreview>('/v1/join/preview', { method: 'POST', body: JSON.stringify({ token }) }),

  /** Signed in (the workspace role not needed): where this person stands with the link's production. */
  joinStatus: (token: string) =>
    request<JoinerView>('/v1/join/status', { method: 'POST', body: JSON.stringify({ token }) }),

  requestToJoin: (token: string) =>
    request<JoinerView>('/v1/join/request', { method: 'POST', body: JSON.stringify({ token }) }),

  myJoinRequests: () => request<{ requests: MyJoinRequest[] }>('/v1/join/requests'),

  /** Becomes a member when an approval was waiting for the workspace to be enabled. */
  enterJoinedProduction: (requestId: string) =>
    request<EnterResult>(`/v1/join/requests/${encodeURIComponent(requestId)}/enter`, { method: 'POST' }),

  joinLink: (projectId: string) => request<JoinLinkView>(`/v1/projects/${projectId}/join-link`),

  /** The ONE answer carrying the link with its token. `replace` stops the live one. */
  generateJoinLink: (projectId: string, replace: boolean) =>
    request<JoinLinkCreated>(`/v1/projects/${projectId}/join-link`, {
      method: 'POST',
      body: JSON.stringify({ replace }),
    }),

  revokeJoinLink: (projectId: string) =>
    request<{ revoked: true }>(`/v1/projects/${projectId}/join-link`, { method: 'DELETE' }),

  joinRequests: (projectId: string) => request<JoinRequestList>(`/v1/projects/${projectId}/join-requests`),

  approveJoinRequest: (projectId: string, requestId: string, authorizationRole: string) =>
    request<JoinDecisionResult>(
      `/v1/projects/${projectId}/join-requests/${encodeURIComponent(requestId)}/approve`,
      { method: 'POST', body: JSON.stringify({ authorizationRole }) },
    ),

  rejectJoinRequest: (projectId: string, requestId: string) =>
    request<JoinDecisionResult>(
      `/v1/projects/${projectId}/join-requests/${encodeURIComponent(requestId)}/reject`,
      { method: 'POST' },
    ),

  projectTransitions: (projectId: string) =>
    request<{
      lifecyclePhase: string;
      allowedLifecyclePhases: string[];
      operationalPhases: string[];
      healthValues: string[];
    }>(`/v1/projects/${projectId}/transitions`),

  setLifecyclePhase: (projectId: string, lifecyclePhase: string) =>
    request<{ lifecyclePhase: string }>(`/v1/projects/${projectId}/lifecycle-phase`, {
      method: 'PATCH',
      body: JSON.stringify({ lifecyclePhase }),
    }),

  setOperationalPhase: (projectId: string, operationalPhase: string | null, reason: string | null) =>
    request<{ operationalPhase: string | null }>(`/v1/projects/${projectId}/operational-phase`, {
      method: 'PATCH',
      body: JSON.stringify({ operationalPhase, reason }),
    }),

  setHealthOverride: (projectId: string, healthOverride: string | null, reason: string | null) =>
    request<{ healthOverride: string | null }>(`/v1/projects/${projectId}/health`, {
      method: 'PATCH',
      body: JSON.stringify({ healthOverride, reason }),
    }),

  listProjects: () => request<Project[]>('/v1/projects'),

  createProject: (input: {
    title: string;
    format: ProductionFormat;
    productionModel: ProductionModel;
    lifecyclePhase?: LifecyclePhase;
  }) =>
    request<{ id: string; urlKey: string }>('/v1/projects', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** With `options`, the upload reports progress and can be cancelled (`uploadWithProgress`). */
  uploadScript: (projectId: string, file: File, options?: UploadOptions) => {
    const form = new FormData();
    form.append('file', file);
    const path = `/v1/projects/${projectId}/scripts`;
    type Uploaded = { scriptVersionId: string; pipelineRunId: string; created: boolean };

    return options === undefined
      ? request<Uploaded>(path, { method: 'POST', body: form })
      : uploadWithProgress<Uploaded>(path, form, options);
  },

  pasteScript: (projectId: string, text: string) =>
    request<{ scriptVersionId: string; pipelineRunId: string; created: boolean }>(
      `/v1/projects/${projectId}/scripts/paste`,
      { method: 'POST', body: JSON.stringify({ text }) },
    ),

  /** The project's current screenplay. Null for a project with no script —
   *  an empty project is a normal state, not an error. */
  searchInventory: (
    projectId: string,
    filter: { q?: string; country?: string; typeId?: number; includePois?: boolean },
  ) => {
    const params = new URLSearchParams();
    if (filter.q) params.set('q', filter.q);
    if (filter.country) params.set('country', filter.country);
    if (filter.typeId !== undefined) params.set('typeId', String(filter.typeId));
    if (filter.includePois === false) params.set('includePois', 'false');

    return request<InventorySearch>(`/v1/projects/${projectId}/inventory?${params.toString()}`);
  },

  inventoryFacets: (projectId: string) =>
    request<{
      countries: { code: string; name: string }[];
      types: { id: number; name: string }[];
    }>(`/v1/projects/${projectId}/inventory/facets`),

  attachCandidate: (
    projectId: string,
    requirementId: string,
    sourceType: 'LOCATION' | 'POI',
    sourceId: number,
  ) =>
    request<{ id: string; created: boolean; restored: boolean }>(
      `/v1/projects/${projectId}/requirements/${requirementId}/candidates`,
      { method: 'POST', body: JSON.stringify({ sourceType, sourceId }) },
    ),

  billingState: (projectId: string) =>
    request<ProjectEntitlement>(`/v1/projects/${projectId}/billing`),

  billingPlans: (projectId: string) =>
    request<{ plans: Plan[] }>(`/v1/projects/${projectId}/billing/plans`),

  /** The same catalogue with no production attached — for the new-project
   *  wizard, which asks what the tiers cost before there is a project to scope
   *  the question to. */
  plans: () => request<{ plans: Plan[] }>('/v1/plans'),

  startPurchase: (projectId: string, planCode: string, termMonths: number) =>
    request<StartedPurchase>(`/v1/projects/${projectId}/billing/purchase`, {
      method: 'POST',
      body: JSON.stringify({ planCode, termMonths }),
    }),

  // ---- The References library (docs/superpowers/specs/2026-11-02-workspace-references-design.md) ----

  /** One page of the library; every filter combines with AND. */
  referenceLibrary: (projectId: string, query: ReferenceLibraryQuery = {}) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      params.set(key, String(value));
    }
    const qs = params.toString();
    return request<ReferenceListPage>(`/v1/projects/${projectId}/references${qs ? `?${qs}` : ''}`);
  },

  /** The sidebar's groups, the chips' counts, the tags and the upload limit — from the server, never the SPA. */
  referenceMeta: (projectId: string) =>
    request<ReferenceLibraryMeta>(`/v1/projects/${projectId}/references/meta`),

  /** With `large`, the detail's preview carries the large re-encoded image. */
  reference: (projectId: string, referenceId: string, options: { large?: boolean } = {}) =>
    request<ReferenceDetail>(
      `/v1/projects/${projectId}/references/${referenceId}${options.large === true ? '?previewSize=large' : ''}`,
    ),

  /** A link or a note. Idempotent on `clientRequestId`: a retry returns the same reference. */
  createReference: (projectId: string, input: CreateReferenceInput) =>
    request<ReferenceDetail>(`/v1/projects/${projectId}/references`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * ONE FILE PER REQUEST, with its own byte progress. `poster` is a frame the
   * browser took from a video; `metadata` carries the `clientRequestId` that
   * makes a retry after a lost answer return the reference it already made.
   */
  uploadReference: (
    projectId: string,
    file: File,
    metadata: UploadReferenceMetadata,
    options: UploadOptions & { poster?: Blob | null } = {},
  ) => {
    const form = new FormData();
    form.append('metadata', JSON.stringify(metadata));
    form.append('file', file);
    if (options.poster) form.append('poster', options.poster, 'poster.jpg');
    return uploadWithProgress<ReferenceDetail>(`/v1/projects/${projectId}/references/files`, form, options);
  },

  /** Server-side read of a link's title, description and image; every field may come back null. */
  referenceLinkPreview: (projectId: string, url: string) =>
    request<LinkPreview>(`/v1/projects/${projectId}/references/link-preview`, {
      method: 'POST',
      body: JSON.stringify({ url }),
    }),

  referenceLinkTargets: (projectId: string, type: ReferenceLinkTargetType, q: string) => {
    const params = new URLSearchParams({ type });
    if (q.trim() !== '') params.set('q', q.trim());
    return request<ReferenceLinkTargetOption[]>(`/v1/projects/${projectId}/references/link-targets?${params.toString()}`);
  },

  /** Compare-and-swap on `expectedVersion`: a stale write is 409 `REFERENCE_CHANGED`. */
  updateReference: (projectId: string, referenceId: string, input: UpdateReferenceInput) =>
    request<ReferenceDetail>(`/v1/projects/${projectId}/references/${referenceId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  setReferenceGroups: (projectId: string, referenceId: string, groupIds: readonly string[]) =>
    request<ReferenceDetail>(`/v1/projects/${projectId}/references/${referenceId}/groups`, {
      method: 'PUT',
      body: JSON.stringify({ groupIds: [...groupIds] }),
    }),

  setReferenceTags: (projectId: string, referenceId: string, tags: readonly string[]) =>
    request<ReferenceDetail>(`/v1/projects/${projectId}/references/${referenceId}/tags`, {
      method: 'PUT',
      body: JSON.stringify({ tags: [...tags] }),
    }),

  // The contract names no answer for the four writes below; the screens re-read
  // the reference (or the library) afterwards instead of trusting a shape.
  addReferenceLink: (projectId: string, referenceId: string, link: ReferenceLinkInput) =>
    request<unknown>(`/v1/projects/${projectId}/references/${referenceId}/links`, {
      method: 'POST',
      body: JSON.stringify(link),
    }),

  removeReferenceLink: (projectId: string, referenceId: string, linkId: string) =>
    request<unknown>(`/v1/projects/${projectId}/references/${referenceId}/links/${linkId}`, {
      method: 'DELETE',
    }),

  retryReferencePreview: (projectId: string, referenceId: string) =>
    request<unknown>(`/v1/projects/${projectId}/references/${referenceId}/preview/retry`, {
      method: 'POST',
    }),

  removeReference: (projectId: string, referenceId: string) =>
    request<unknown>(`/v1/projects/${projectId}/references/${referenceId}`, {
      method: 'DELETE',
    }),

  createReferenceGroup: (projectId: string, input: { clientRequestId: string; name: string }) =>
    request<unknown>(`/v1/projects/${projectId}/reference-groups`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  renameReferenceGroup: (projectId: string, groupId: string, name: string) =>
    request<unknown>(`/v1/projects/${projectId}/reference-groups/${groupId}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),

  /** The group and its memberships only — the references stay in the library. */
  deleteReferenceGroup: (projectId: string, groupId: string) =>
    request<unknown>(`/v1/projects/${projectId}/reference-groups/${groupId}`, {
      method: 'DELETE',
    }),

  addToReferenceGroup: (projectId: string, groupId: string, referenceIds: readonly string[]) =>
    request<unknown>(`/v1/projects/${projectId}/reference-groups/${groupId}/references`, {
      method: 'POST',
      body: JSON.stringify({ referenceIds: [...referenceIds] }),
    }),

  projectSetup: (projectId: string) =>
    request<ProjectSetup>(`/v1/projects/${projectId}/setup`),

  /**
   * The Production Command Center: the facts, and the server's answer to "what
   * next" derived from them (`deriveOverview`). The SPA renders it and computes
   * nothing, as with `projectSetup`.
   */
  projectOverview: (projectId: string) =>
    request<ProjectOverview>(`/v1/projects/${projectId}/overview`),

  /** The Overview's recent activity: read from the records the production keeps. */
  overviewActivity: (projectId: string) =>
    request<{ items: OverviewActivityItem[] }>(`/v1/projects/${projectId}/overview/activity`),

  /** The producer passed the scene overview — a bookmark, not a review. */
  markScenesOverviewCompleted: (projectId: string) =>
    request<ProjectSetup>(`/v1/projects/${projectId}/setup/scenes-overview-completed`, {
      method: 'POST',
    }),

  /**
   * Go on to locations with the breakdown not yet complete. Undecided
   * requirements stay as they are; refused 409 `NO_BREAKDOWN_TO_CONTINUE` when
   * there is not one requirement to go on with.
   */
  /**
   * "Confirm scenes & continue". The server applies the rules: a scene needing
   * attention refuses it (409 `SCENES_NEED_ATTENTION`), and scenes nobody
   * confirmed refuse it (409 `SCENES_NOT_REVIEWED`) unless `confirmRemaining`.
   */
  confirmScenesAndContinue: (projectId: string, input: { scriptVersionId: string; confirmRemaining: boolean }) =>
    request<ProjectSetup>(`/v1/projects/${projectId}/setup/scenes-confirmed`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  markBreakdownContinued: (projectId: string) =>
    request<ProjectSetup>(`/v1/projects/${projectId}/setup/breakdown-continued`, {
      method: 'POST',
    }),

  /** The producer finished the guided path and entered the workspace. */
  markSetupCompleted: (projectId: string) =>
    request<ProjectSetup>(`/v1/projects/${projectId}/setup/completed`, { method: 'POST' }),

  currentScript: (projectId: string) =>
    request<{
      scriptId: string;
      scriptVersionId: string;
      title: string;
      versionNumber: number;
      sceneCount: number;
      createdAt: string | null;
    } | null>(`/v1/projects/${projectId}/script`),

  /** Accept, reject or correct one AI-proposed element. The decision is
   *  appended, never overwritten — the mention it decides about is immutable. */
  decideElement: (
    projectId: string,
    mentionId: string,
    // RESTORE puts back something previously rejected — the server has always
    // accepted it (`DecideBody`), the client just never offered it, so a
    // rejection was one-way from every screen.
    input: { action: 'ACCEPT' | 'EDIT' | 'REJECT' | 'RESTORE'; editedName?: string },
  ) =>
    request<{ productionElementId: string | null; rowVersion: number }>(
      `/v1/projects/${projectId}/breakdown/mentions/${mentionId}/decide`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  sceneBreakdown: (projectId: string, sceneId: string) =>
    request<SceneBreakdown>(`/v1/projects/${projectId}/breakdown/scenes/${sceneId}`),

  addSceneElement: (projectId: string, sceneId: string, category: string, name: string) =>
    request<{ id: string }>(`/v1/projects/${projectId}/breakdown/scenes/${sceneId}/elements`, {
      method: 'POST',
      body: JSON.stringify({ category, name }),
    }),

  /**
   * One element on several scenes — "every scene it appears in" (owner review
   * 2026-10-27, D2). `sceneIds` is the list the producer was shown; all or none.
   */
  addElementToScenes: (projectId: string, input: { category: string; name: string; sceneIds: readonly string[] }) =>
    request<{ id: string; linked: number; alreadyOnScene: number }>(`/v1/projects/${projectId}/breakdown/elements`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** One element off several scenes — the same question, put to un-tagging. */
  removeElementFromScenes: (projectId: string, productionElementId: string, sceneIds: readonly string[]) =>
    request<{ removed: number }>(
      `/v1/projects/${projectId}/breakdown/elements/${productionElementId}/remove-from-scenes`,
      { method: 'POST', body: JSON.stringify({ sceneIds }) },
    ),

  addRequirement: (
    projectId: string,
    input: {
      title: string;
      /** Predates the multi-scene form; still accepted by the API. */
      sceneId?: string;
      /** A location requirement is shared by construction — "Hotel room" is
       *  scenes 3, 8, 14 and 21 — so the scope is a set, not one scene. */
      sceneIds?: readonly string[];
      creativeDescription?: string;
    },
  ) =>
    request<{ id: string }>(`/v1/projects/${projectId}/requirements`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  listScenes: (scriptVersionId: string) =>
    request<Scene[]>(`/v1/script-versions/${scriptVersionId}/scenes`),

  /** `null` when nothing has been run on this version — a normal answer, not
   *  an error: a screenplay that parsed before the tab asked has nothing in
   *  flight. */
  scriptProgress: (scriptVersionId: string) =>
    request<ScriptProgress | null>(`/v1/script-versions/${scriptVersionId}/progress`),


  /** What the producer told us, what we would suggest, and the country catalogue. */
  searchParameters: (projectId: string) =>
    request<SearchParametersView>(`/v1/projects/${projectId}/search-parameters`),

  /** Saving an EMPTY set is a real answer — "use script requirements only". */
  saveSearchParameters: (
    projectId: string,
    input: Partial<Omit<SearchParametersState, 'setAt'>>,
  ) =>
    request<SearchParametersState>(`/v1/projects/${projectId}/search-parameters`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** Every matched location, once, however many requirements it serves. */
  matchedLocations: (projectId: string) =>
    request<MatchedView>(`/v1/projects/${projectId}/matched`),

  /**
   * Every public photograph of one matched location — the gallery its listing
   * page shows, cover first. 404 for anything that is not a listed candidate
   * of this production.
   */
  locationPhotos: (projectId: string, locationId: number) =>
    request<{ photos: { url: string; cover: boolean }[] }>(
      `/v1/projects/${projectId}/matched/locations/${String(locationId)}/photos`,
    ),

  mergeSuggestions: (projectId: string) =>
    request<MergeSuggestion[]>(`/v1/projects/${projectId}/matched/merge-suggestions`),

  /** The whole review screen in one read — see the route's own docblock. */
  reviewView: (scriptVersionId: string) =>
    request<ReviewView>(`/v1/script-versions/${scriptVersionId}/review`),

  /**
   * "I have read this scene's breakdown and it is right."
   *
   * Until this, an untouched AI item means nothing — the producer may never
   * have opened that category — so nothing counts as an accepted prediction.
   */
  markSceneReviewed: (projectId: string, sceneId: string) =>
    request<{ reviewStatus: string }>(
      `/v1/projects/${projectId}/breakdown/scenes/${sceneId}/review`,
      { method: 'POST', body: JSON.stringify({}) },
    ),

  /**
   * How many of this element the scene needs.
   *
   * "Police car" and "six police cars" are different days, different budgets and
   * different call sheets. Null clears it back to uncounted.
   */
  setElementQuantity: (
    projectId: string,
    sceneId: string,
    productionElementId: string,
    quantity: number | null,
  ) =>
    request<{ quantity: number | null }>(
      `/v1/projects/${projectId}/breakdown/scenes/${sceneId}/elements/${productionElementId}`,
      { method: 'PATCH', body: JSON.stringify({ quantity }) },
    ),

  /**
   * Take an element off this scene's sheet.
   *
   * Unlinks from the SCENE, not from the production: the same prop legitimately
   * appears in other scenes, and "not in this one" is not "not in this film".
   */
  removeSceneElement: (projectId: string, sceneId: string, productionElementId: string) =>
    request<{ removed: boolean }>(
      `/v1/projects/${projectId}/breakdown/scenes/${sceneId}/elements/${productionElementId}`,
      { method: 'DELETE' },
    ),

  /**
   * A scene the parser missed.
   *
   * The scene split is a reading like any other, and it was the one a producer
   * could not correct. `afterSceneId` places it; null puts it first.
   */
  addScene: (
    projectId: string,
    input: {
      scriptVersionId: string;
      afterSceneId: string | null;
      slugline: string;
      sceneNumber?: string | null;
      synopsis?: string | null;
      /** What happens in the scene, typed — what the AI breakdown will read. */
      text?: string | null;
    },
  ) =>
    request<{ id: string; ordinal: number }>(`/v1/projects/${projectId}/breakdown/scenes`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * Put a scene somewhere else in the running order.
   *
   * `afterSceneId` is the scene it should follow; null puts it first. The server
   * renumbers the whole version, so the answer is the order every surface reads.
   */
  moveScene: (projectId: string, sceneId: string, afterSceneId: string | null) =>
    request<{ ordinal: number }>(
      `/v1/projects/${projectId}/breakdown/scenes/${sceneId}/position`,
      { method: 'PATCH', body: JSON.stringify({ afterSceneId }) },
    ),

  /**
   * A scene that is not one. Soft, like everything here — and it reports how
   * many requirements it unlinked, and how many of those now point at no scene
   * at all, so the screen can say so rather than leaving it to be discovered.
   */
  removeScene: (projectId: string, sceneId: string) =>
    request<{ removed: boolean; requirementsUnlinked: number; requirementsLeftWithNoScene: number }>(
      `/v1/projects/${projectId}/breakdown/scenes/${sceneId}`,
      { method: 'DELETE' },
    ),

  /** Step 2's one read: every scene with its validation status, and the counts. */
  sceneValidation: (projectId: string, scriptVersionId: string) =>
    request<SceneValidationView>(
      `/v1/projects/${projectId}/breakdown/scene-validation?scriptVersionId=${encodeURIComponent(scriptVersionId)}`,
    ),

  /**
   * Correct a scene's heading, its number, INT./EXT. or time of day — and, on a
   * scene a person added, its text. Only the fields sent change.
   */
  editScene: (
    projectId: string,
    sceneId: string,
    input: {
      slugline?: string;
      sceneNumber?: string | null;
      intExt?: SceneIntExt | null;
      timeOfDay?: string | null;
      text?: string | null;
    },
  ) =>
    request<{ id: string; contentChanged: boolean }>(`/v1/projects/${projectId}/breakdown/scenes/${sceneId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  /** Confirm scenes, or take the confirmation back — one or a selection, all or none. */
  setScenesConfirmed: (
    projectId: string,
    input: { scriptVersionId: string; sceneIds: readonly string[]; confirmed: boolean },
  ) =>
    request<{ updated: number }>(`/v1/projects/${projectId}/breakdown/scenes/confirmation`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** A new scene starts at `at`, a line start in this scene's text. */
  splitScene: (projectId: string, sceneId: string, input: { at: number; slugline?: string | null }) =>
    request<{ id: string; ordinal: number }>(`/v1/projects/${projectId}/breakdown/scenes/${sceneId}/split`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** The scene after this one joins it. */
  mergeNextScene: (projectId: string, sceneId: string) =>
    request<{ mergedSceneId: string }>(`/v1/projects/${projectId}/breakdown/scenes/${sceneId}/merge-next`, {
      method: 'POST',
    }),

  /** Put back a scene somebody excluded as "not a scene". */
  restoreScene: (projectId: string, sceneId: string) =>
    request<{ id: string; ordinal: number }>(`/v1/projects/${projectId}/breakdown/scenes/${sceneId}/restore`, {
      method: 'POST',
    }),

  /**
   * Take a scene back out of reviewed.
   *
   * The tick was one-way: a producer who marked a scene and then spotted a
   * missed location had no way back from the card. A review is a bookmark over
   * a human pass, not an approval later work depends on.
   */
  unmarkSceneReviewed: (projectId: string, sceneId: string) =>
    request<{ reviewStatus: string }>(
      `/v1/projects/${projectId}/breakdown/scenes/${sceneId}/review`,
      { method: 'DELETE' },
    ),

  /** The same, for a shared location profile — reviewed once, not per scene. */
  markProfileReviewed: (projectId: string, requirementId: string) =>
    request<{ reviewStatus: string }>(
      `/v1/projects/${projectId}/requirements/${requirementId}/review`,
      { method: 'POST', body: JSON.stringify({}) },
    ),

  /**
   * A demand on the location, written from the screenplay's own words.
   *
   * The facet says WHICH QUESTION it answers — the place itself, how it must
   * look, what it must physically have, what must be true to shoot there — and
   * is a different axis from the kind, which says how badly it is wanted.
   */
  addConstraint: (
    projectId: string,
    requirementId: string,
    input: { facet: string; label: string; kind?: string },
  ) =>
    request<{ id: string }>(
      `/v1/projects/${projectId}/requirements/${requirementId}/constraints`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  /** Two requirements that are really one place. */
  mergeRequirements: (projectId: string, requirementIds: readonly string[]) =>
    request<{ survivorId: string; absorbed: number }>(
      `/v1/projects/${projectId}/requirements/merge`,
      { method: 'POST', body: JSON.stringify({ requirementIds }) },
    ),

  /** One requirement that is really two. */
  splitRequirement: (
    projectId: string,
    requirementId: string,
    input: { title: string; sceneIds: readonly string[] },
  ) =>
    request<{ id: string }>(`/v1/projects/${projectId}/requirements/${requirementId}/split`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  reviewProgress: (projectId: string) =>
    request<ReviewProgress>(`/v1/projects/${projectId}/requirements/progress`),

  listRequirements: (projectId: string) =>
    request<ReviewRequirement[]>(`/v1/projects/${projectId}/requirements`),

  matchableRequirements: (projectId: string) =>
    request<Requirement[]>(`/v1/projects/${projectId}/requirements/matchable`),

  approveRequirement: (projectId: string, id: string, expectedVersion: number) =>
    request<{ rowVersion: number; status: string }>(
      `/v1/projects/${projectId}/requirements/${id}/approve`,
      { method: 'POST', body: JSON.stringify({ expectedVersion }) },
    ),

  /** `reason` is optional (BI gap 25): only the Sourcing list asks for one. */
  dismissRequirement: (
    projectId: string,
    id: string,
    expectedVersion: number,
    reason: { reasonCode?: string; note?: string } = {},
  ) =>
    request<{ rowVersion: number }>(`/v1/projects/${projectId}/requirements/${id}/dismiss`, {
      method: 'POST',
      body: JSON.stringify({ expectedVersion, ...reason }),
    }),

  /** Undo a dismissal — back to awaiting a decision, not to approved. */
  restoreRequirement: (projectId: string, id: string, expectedVersion: number) =>
    request<{ rowVersion: number; status: string }>(
      `/v1/projects/${projectId}/requirements/${id}/restore`,
      { method: 'POST', body: JSON.stringify({ expectedVersion }) },
    ),

  setSourcing: (
    projectId: string,
    id: string,
    expectedVersion: number,
    sourcingStatus: RequirementSourcingStatus,
  ) =>
    request<{ rowVersion: number; sourcingStatus: string }>(
      `/v1/projects/${projectId}/requirements/${id}/sourcing`,
      { method: 'PATCH', body: JSON.stringify({ expectedVersion, sourcingStatus }) },
    ),

  runMatching: (projectId: string, requirementId: string) =>
    request<{ matchRunId: string; assessed: number; suggested: number }>(
      `/v1/projects/${projectId}/requirements/${requirementId}/match`,
      { method: 'POST' },
    ),

  compareView: (projectId: string, requirementId: string) =>
    request<CompareView>(`/v1/projects/${projectId}/requirements/${requirementId}/compare`),

  setCandidateStatus: (
    projectId: string,
    candidateId: string,
    expectedVersion: number,
    status: 'LONGLIST' | 'SHORTLIST' | 'REJECTED' | 'ARCHIVED',
    reason?: string,
  ) =>
    request<{ rowVersion: number; status: string }>(
      `/v1/projects/${projectId}/candidates/${candidateId}/status`,
      { method: 'PATCH', body: JSON.stringify({ expectedVersion, status, reason }) },
    ),

  confirmSelection: (
    projectId: string,
    requirementId: string,
    input: {
      primaryCandidateId: string;
      backupCandidateId?: string | null;
      expectedVersion: number;
      acknowledgeStaleMatch?: boolean;
    },
  ) =>
    request<{ rowVersion: number }>(
      `/v1/projects/${projectId}/requirements/${requirementId}/selection`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  scheduleBoard: (projectId: string) => request<unknown[]>(`/v1/projects/${projectId}/schedule`),

  scheduleWarnings: (projectId: string) =>
    request<ScheduleWarning[]>(`/v1/projects/${projectId}/schedule/warnings`),

  listPois: (projectId: string) => request<unknown[]>(`/v1/projects/${projectId}/pois`),

  listRequests: (projectId: string) =>
    request<WorkspaceRequest[]>(`/v1/projects/${projectId}/requests`),

  /**
   * File a request with the ScoutAround team.
   *
   * The unmatched-requirements path uses `FIND_LOCATION`, whose catalogue entry
   * already requires REQUIREMENT targets — so sourcing help is the existing
   * concierge channel rather than a second one that would need its own queue,
   * its own SLA and its own place for a producer to look.
   */
  createRequest: (
    projectId: string,
    input: {
      type: string;
      title: string;
      body?: string;
      targets: { targetType: string; targetId: string }[];
    },
  ) =>
    request<{ id: string; reference: string }>(`/v1/projects/${projectId}/requests`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  listMembers: (projectId: string) => request<Member[]>(`/v1/projects/${projectId}/members`),

  /** `TEAM_MANAGE`. The server refuses a change that would leave nobody able to
   *  edit the project (`LAST_OWNER`); the credit is not changed here. */
  setMemberRole: (projectId: string, memberId: string, authorizationRole: string) =>
    request<{ authorizationRole: string }>(`/v1/projects/${projectId}/members/${memberId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ authorizationRole }),
    }),

  /** What the signed-in person may do IN THIS PROJECT. Never cached per user. */
  myMembership: (projectId: string) =>
    request<MyMembership>(`/v1/projects/${projectId}/me`),

  rolePermissions: (projectId: string) =>
    request<RolePermissionGrid>(`/v1/projects/${projectId}/role-permissions`),

  /** One cell at a time — see the controller for why this is not a bulk PUT. */
  setRolePermission: (
    projectId: string,
    cell: { role: string; permission: string; state: 'GRANTED' | 'REVOKED' | 'DEFAULT' },
  ) =>
    request<RolePermissionGrid>(`/v1/projects/${projectId}/role-permissions`, {
      method: 'PUT',
      body: JSON.stringify(cell),
    }),

  listInvitations: (projectId: string) =>
    request<Invitation[]>(`/v1/projects/${projectId}/invitations`),

  invite: (projectId: string, input: { email: string; authorizationRole: string; productionTitle?: string }) =>
    // No `hasAccount`: whether an address has an account is not the inviter's
    // to know (the server stopped sending it).
    request<{ id: string; email: string; expiresAt: string }>(
      `/v1/projects/${projectId}/invitations`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  revokeInvitation: (projectId: string, invitationId: string) =>
    request<{ status: string }>(`/v1/projects/${projectId}/invitations/${invitationId}`, {
      method: 'DELETE',
    }),

  listActivity: (projectId: string) =>
    request<ActivityEntry[]>(`/v1/projects/${projectId}/activity`),

  listFiles: (projectId: string) => request<ProjectFile[]>(`/v1/projects/${projectId}/files`),

  /** The upload limit, the accepted types, the plan and the read-only state — from the server, never the SPA. */
  filesMeta: (projectId: string) => request<ProjectFilesMeta>(`/v1/projects/${projectId}/files/meta`),

  /** Soft delete (ADR 0011): the file leaves the list and ScoutAround keeps it. */
  deleteFile: (projectId: string, fileId: string) =>
    request<ProjectFileDeleted>(`/v1/projects/${projectId}/files/${encodeURIComponent(fileId)}`, { method: 'DELETE' }),

  listConversations: (projectId: string) =>
    request<ProjectConversation[]>(`/v1/projects/${projectId}/chat`),

  /**
   * ONE FILE PER REQUEST, with its own byte progress. `clientRequestId` is minted once
   * per file and sent again on Retry, so an upload whose answer was lost returns the
   * file the server already stored instead of a second copy.
   */
  uploadFile: (
    projectId: string,
    file: File,
    fields: { clientRequestId?: string } = {},
    options: UploadOptions = {},
  ) => {
    const form = new FormData();
    if (fields.clientRequestId !== undefined) form.append('clientRequestId', fields.clientRequestId);
    form.append('file', file);
    return uploadWithProgress<{ id: string }>(`/v1/projects/${projectId}/files`, form, options);
  },

  strips: (projectId: string) => request<Strip[]>(`/v1/projects/${projectId}/schedule/strips`),

  dood: (projectId: string) => request<Dood>(`/v1/projects/${projectId}/schedule/dood`),

  revisionBlocked: (projectId: string) =>
    request<{ blocked: boolean }>(`/v1/projects/${projectId}/revisions/blocked`),

  // ---- Team conversations (ADR 0021) -----------------------------------------

  conversations: (
    projectId: string,
    query: {
      anchorType?: ConversationAnchorType;
      anchorId?: string;
      filter?: ConversationListFilter;
      cursor?: string;
    } = {},
  ) => {
    const params = new URLSearchParams();
    if (query.anchorType && query.anchorId) {
      params.set('anchorType', query.anchorType);
      params.set('anchorId', query.anchorId);
    }
    if (query.filter) params.set('filter', query.filter);
    if (query.cursor) params.set('cursor', query.cursor);
    const qs = params.toString();
    return request<{ threads: ConversationThreadView[]; nextCursor: string | null }>(
      `/v1/projects/${projectId}/conversations${qs ? `?${qs}` : ''}`,
    );
  },

  conversationSummary: (projectId: string) =>
    request<ConversationSummary>(`/v1/projects/${projectId}/conversations/summary`),

  /** The places and script locations a conversation may point at, as this reader sees them. */
  conversationLocations: (projectId: string) =>
    request<ConversationLocations>(`/v1/projects/${projectId}/conversations/locations`),

  conversation: (projectId: string, threadId: string) =>
    request<ConversationThreadView>(`/v1/projects/${projectId}/conversations/${threadId}`),

  conversationMessages: (projectId: string, threadId: string, page: { after?: number; before?: number } = {}) => {
    const params = new URLSearchParams();
    if (page.after !== undefined) params.set('after', String(page.after));
    if (page.before !== undefined) params.set('before', String(page.before));
    const qs = params.toString();
    return request<{ messages: ConversationMessageView[]; lastSeq: number }>(
      `/v1/projects/${projectId}/conversations/${threadId}/messages${qs ? `?${qs}` : ''}`,
    );
  },

  createConversation: (
    projectId: string,
    input: {
      clientRequestId: string;
      anchorType: ConversationAnchorType;
      anchorId: string;
      title: string;
      visibility: ConversationVisibility;
      body: string;
      participantUserIds: number[];
    },
  ) =>
    request<ConversationThreadView>(`/v1/projects/${projectId}/conversations`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  postConversationMessage: (projectId: string, threadId: string, input: { clientMessageId: string; body: string }) =>
    request<ConversationMessageView>(`/v1/projects/${projectId}/conversations/${threadId}/messages`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  renameConversation: (projectId: string, threadId: string, title: string) =>
    request<ConversationThreadView>(`/v1/projects/${projectId}/conversations/${threadId}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    }),

  resolveConversation: (projectId: string, threadId: string) =>
    request<ConversationThreadView>(`/v1/projects/${projectId}/conversations/${threadId}/resolve`, {
      method: 'POST',
    }),

  reopenConversation: (projectId: string, threadId: string) =>
    request<ConversationThreadView>(`/v1/projects/${projectId}/conversations/${threadId}/reopen`, {
      method: 'POST',
    }),

  addConversationParticipants: (projectId: string, threadId: string, userIds: number[]) =>
    request<ConversationThreadView>(`/v1/projects/${projectId}/conversations/${threadId}/participants`, {
      method: 'POST',
      body: JSON.stringify({ userIds }),
    }),

  removeConversationParticipant: (projectId: string, threadId: string, userId: number) =>
    request<{ left: boolean }>(
      `/v1/projects/${projectId}/conversations/${threadId}/participants/${String(userId)}/remove`,
      { method: 'POST' },
    ),

  leaveConversation: (projectId: string, threadId: string) =>
    request<{ left: boolean }>(`/v1/projects/${projectId}/conversations/${threadId}/leave`, {
      method: 'POST',
    }),

  markConversationRead: (projectId: string, threadId: string, seq: number) =>
    request<undefined>(`/v1/projects/${projectId}/conversations/${threadId}/read`, {
      method: 'POST',
      body: JSON.stringify({ seq }),
    }),

  followConversation: (projectId: string, threadId: string, state: ConversationFollowState) =>
    request<undefined>(`/v1/projects/${projectId}/conversations/${threadId}/follow`, {
      method: 'PUT',
      body: JSON.stringify({ state }),
    }),

  conversationEmail: (projectId: string) =>
    request<{ email: ConversationEmailPreference }>(`/v1/projects/${projectId}/conversations/email`),

  setConversationEmail: (projectId: string, email: ConversationEmailPreference) =>
    request<{ email: ConversationEmailPreference }>(`/v1/projects/${projectId}/conversations/email`, {
      method: 'PUT',
      body: JSON.stringify({ email }),
    }),

  withdrawConversationMessage: (projectId: string, messageId: string) =>
    request<undefined>(`/v1/projects/${projectId}/conversations/messages/${messageId}/withdraw`, {
      method: 'POST',
    }),

  hideConversationMessage: (projectId: string, messageId: string, reason: string) =>
    request<undefined>(`/v1/projects/${projectId}/conversations/messages/${messageId}/hide`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  reportConversationMessage: (
    projectId: string,
    messageId: string,
    input: { reasonCode: ConversationReportReason; note?: string },
  ) =>
    request<undefined>(`/v1/projects/${projectId}/conversations/messages/${messageId}/report`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  sceneCarryover: (projectId: string, scriptVersionId: string) =>
    request<{
      placed: { sceneId: string; verdict: 'CERTAIN' | 'LIKELY'; threads: ConversationThreadView[] }[];
      unplaced: ConversationThreadView[];
    }>(`/v1/projects/${projectId}/conversations/scene-carryover?scriptVersionId=${encodeURIComponent(scriptVersionId)}`),

  projectPeople: (projectId: string, anchorType?: ConversationAnchorType) =>
    request<{ people: ProjectPerson[] }>(
      `/v1/projects/${projectId}/people${anchorType ? `?anchorType=${anchorType}` : ''}`,
    ),

  unreadConversations: () =>
    request<{ projects: { projectId: string; unread: number; mentions: number }[] }>('/v1/conversations/unread'),

  runAiTool: (projectId: string, action: AiToolAction, extra: Record<string, string> = {}) =>
    request<{ action: string; claims: { text: string; provenance: string }[] }>(
      `/v1/projects/${projectId}/ai-tools/run`,
      { method: 'POST', body: JSON.stringify({ action, ...extra }) },
    ),
};
